import React, { Component } from 'react';
import {
  includes,
  keyBy,
  filter,
  reject,
  get,
  map,
  has,
  find,
  sumBy,
  size,
  values,
  getOr,
  isEmpty
} from 'lodash/fp';
import PropTypes from 'prop-types';
import {
  Schema,
  Form,
  FormControl,
  Button,
  Icon,
  Whisper,
  Alert,
  DatePicker
} from 'rsuite';
import InventoryActions from '../../../constants/inventory/InventoryActions';
import {
  InventoryItemType,
  InventoryPackageType,
  InventoryItemSkuType,
  InventoryAvailabilityType
} from '../../../interfaces/inventory';
import InventoryItemAvailability from '../ItemAvailability';
import InventoryImage from '../Image';
import PackageItemsPreview from '../PackageItemsPreview';
import PackageItemsPopover from '../PackageItemsPopover';
import {
  ControlLabel,
  FormGroup,
  SelectPicker
} from '../../widgets/FormElements';
import ReasonOptions from './reason-options';
import { parseInt } from '../../../helpers/utils';

const {
  ADD,
  REMOVE,
  ORDER_RECEIVED,
  DONATION_RECEIVED,
  DONATION_REMOVED
} = InventoryActions;
const { NumberType, StringType, DateType } = Schema.Types;

const defualtFormValue = {
  action: '',
  locationId: null,
  quantity: '',
  note: '',
  performedOn: new Date()
};

const QtyPrefixByAction = {
  [ADD]: '+',
  [REMOVE]: '-'
};

export default class InventoryAdjustItemForm extends Component {
  static propTypes = {
    action: PropTypes.oneOf(values(InventoryActions)).isRequired,
    locationId: PropTypes.number,
    item: PropTypes.oneOfType([InventoryItemType, InventoryPackageType]),
    skus: PropTypes.arrayOf(InventoryItemSkuType),
    availability: PropTypes.arrayOf(InventoryAvailabilityType),
    packageItems: PropTypes.arrayOf(PropTypes.shape({})),
    donorOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    locationOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    isSaving: PropTypes.bool.isRequired,
    isSaved: PropTypes.bool.isRequired,
    isFetchingDonors: PropTypes.bool.isRequired,
    onInit: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired
  };

  static defaultProps = {
    item: null,
    locationId: null,
    skus: [],
    packageItems: [],
    availability: []
  };

  constructor(props) {
    super(props);
    const { action, locationId } = props;
    this.state = {
      formValue: { ...defualtFormValue, action, locationId },
      formError: {}
    };
    this.formRef = React.createRef();
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    const { onInit } = this.props;
    onInit();
  }

  componentDidUpdate({ isSaved: prevIsSaved }) {
    const { isSaved, onSaved } = this.props;
    if (!prevIsSaved && isSaved) {
      onSaved();
    }
  }

  get model() {
    return Schema.Model({
      action: NumberType().isRequired('This field is required'),
      locationId: NumberType().isRequired('This field is required'),
      quantity: NumberType()
        .addRule((value, { locationId }) => {
          if (!value || !locationId) return true;
          return this.validateQuantity(parseInt(value), locationId);
        }, 'Must be equal or less than available quantity')
        .addRule((value, { locationId }) => {
          if (!value || !locationId) return true;
          return this.validateForOnholdQuantity(parseInt(value), locationId);
        }, 'Must have enough remaining for onhold quantity')
        .isRequired('This field is required'),
      note: StringType(),
      performedOn: DateType().max(
        new Date(),
        'Must not later than current date'
      )
    });
  }

  get quantityAvailable() {
    const { availability } = this.props;
    return sumBy('quantity', availability);
  }

  get isDeductAction() {
    const { action } = this.props;
    return action === REMOVE;
  }

  get locationOptions() {
    const { availability, locationOptions, action } = this.props;
    const actionsWithAllLocations = [ADD, ORDER_RECEIVED, DONATION_RECEIVED];
    const showAllLocations = includes(action, actionsWithAllLocations);
    if (!showAllLocations) {
      const existingLocations = keyBy(get('location.id'), availability);
      return filter(
        ({ value }) => has(value, existingLocations),
        locationOptions
      );
    }
    return locationOptions;
  }

  get destinationLocationOptions() {
    const { formValue } = this.state;
    const { locationOptions } = this.props;
    return reject({ value: formValue.locationId }, locationOptions);
  }

  handleFormChange = formValue => {
    this.setState({ formValue });
  };

  handleSubmit = () => {
    if (!this.formRef.current.check()) return;
    if (this.isDeductAction && this.quantityAvailable < 1) {
      Alert.error(`Unable to proceed. Available quantity is 0`, 5000);
      return;
    }

    const { onSubmit } = this.props;
    const { formValue } = this.state;
    onSubmit(formValue);
  };

  validateQuantity(quantity, locationId) {
    const { action, item, availability } = this.props;
    if (action !== REMOVE) return true;
    const itemAvailability = find(
      each => item.id === each.item.id && each.location.id === locationId,
      availability
    );
    return itemAvailability.quantity >= quantity;
  }

  validateForOnholdQuantity(quantity, locationId) {
    const { action, item, availability } = this.props;
    if (action !== REMOVE) return true;
    const itemAvailability = find(
      each => item.id === each.item.id && each.location.id === locationId,
      availability
    );
    const { onholdQuantity } = itemAvailability;
    return itemAvailability.quantity - quantity >= onholdQuantity;
  }

  render() {
    const {
      action,
      isSaving,
      item,
      skus,
      donorOptions,
      packageItems,
      availability,
      isFetchingDonors
    } = this.props;
    const { formValue, formError } = this.state;

    const isDonation = formValue.action === DONATION_REMOVED;

    return (
      <Form
        style={{ maxWidth: 500 }}
        layout="horizontal"
        model={this.model}
        ref={this.formRef}
        formValue={formValue}
        onChange={this.handleFormChange}
        onCheck={error => {
          this.setState({ formError: error });
        }}
      >
        <div
          style={{
            verticalAlign: 'top',
            paddingBottom: 10,
            marginBottom: 10,
            marginRight: 20,
            borderBottom: '1px solid #ddd'
          }}
        >
          <InventoryImage
            image={item.photo}
            alt="photo"
            style={{ width: 50, float: 'left', marginRight: 20 }}
          />
          <div
            style={{
              display: 'inline-block',
              width: '60%',
              verticalAlign: 'top'
            }}
          >
            <h5 style={{ marginBottom: 10, marginTop: 0 }}>{item.name}</h5>
            {size(skus) > 0 && (
              <span style={{ color: '#8e8e93' }}>
                {map('sku', skus).join(', ')}
              </span>
            )}
            {size(packageItems) > 0 && (
              <div>
                <PackageItemsPreview packageItems={packageItems} />
                <Whisper
                  placement="right"
                  trigger="click"
                  speaker={<PackageItemsPopover packageItems={packageItems} />}
                >
                  <Button size="xs" appearance="link">
                    <Icon icon="external-link-square" />
                  </Button>
                </Whisper>
              </div>
            )}
          </div>
          <div style={{ display: 'inline-block', width: '25%' }}>
            <h4 style={{ marginBottom: 10, marginTop: 0 }}>
              ${parseFloat(item.price).toFixed(2)}
            </h4>
            <span style={{ color: '#8e8e93', marginRight: 10 }}>
              Qty Available:
            </span>
            <span style={{ fontSize: '14px' }}>
              {sumBy('quantity', availability)}
            </span>
          </div>
        </div>
        {size(availability) > 0 && (
          <FormGroup>
            <ControlLabel>Item location</ControlLabel>
            <InventoryItemAvailability
              selected={formValue.locationId}
              availability={availability}
              onSelectItem={() => null}
            />
          </FormGroup>
        )}
        {action === REMOVE && (
          <FormGroup>
            <ControlLabel>Reason</ControlLabel>
            <FormControl
              name="action"
              placeholder="Select reason"
              errorMessage={formError.action}
              accepter={SelectPicker}
              data={ReasonOptions}
            />
          </FormGroup>
        )}
        {isDonation && (
          <FormGroup>
            <ControlLabel>Donor</ControlLabel>
            <FormControl
              name="donorId"
              placeholder={isFetchingDonors ? 'Loading...' : 'Select Donor'}
              accepter={SelectPicker}
              data={donorOptions}
              errorMessage={formError.donorId}
            />
          </FormGroup>
        )}
        <FormGroup>
          <ControlLabel>
            {getOr('', action, QtyPrefixByAction)} Quantity
          </ControlLabel>
          <FormControl name="quantity" defaultValue={0} />
        </FormGroup>
        <FormGroup>
          <ControlLabel>Note</ControlLabel>
          <FormControl rows={5} name="note" componentClass="textarea" />
        </FormGroup>
        <FormGroup>
          <ControlLabel>Date Performed</ControlLabel>
          <FormControl
            name="performedOn"
            accepter={props => <DatePicker oneTap {...props} />}
          />
        </FormGroup>

        <FormGroup style={{ textAlign: 'right', marginRight: 18 }}>
          <Button
            onClick={this.handleSubmit}
            appearance="primary"
            disabled={isSaving || !isEmpty(formError)}
          >
            <Icon icon="save" /> {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </FormGroup>
      </Form>
    );
  }
}
